import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { PropertyModal } from '../components/PropertyModal';
import { CheckCircle2, Star, Home, Users, ShieldCheck, ArrowRight, Search, MapPin, Calendar, User, Quote, Phone, Mail } from 'lucide-react';
import { cn } from '../utils/cn';
export function HomePage() {
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    county: 'All Counties',
    gender: 'Any Gender',
    moveInDate: ''
  });
  const openPropertyDetails = (property: any) => {
    setSelectedProperty(property);
    setIsModalOpen(true);
  };
  const handleSearch = () => {
    navigate('/homes', {
      state: {
        filters: searchFilters
      }
    });
  };
  return <div className="w-full" data-id="element-345">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-12 md:pt-20 pb-20 md:pb-32 overflow-hidden" data-id="element-346">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-full md:w-1/2 h-full bg-secondary/5 -z-10 rounded-bl-[100px] md:rounded-bl-[200px]" data-id="element-347" />
        <div className="absolute top-[-10%] left-[-10%] w-[60%] md:w-[40%] h-[40%] bg-accent/5 blur-[80px] md:blur-[120px] rounded-full -z-10" data-id="element-348" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center" data-id="element-349">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="space-y-6 md:space-y-8" data-id="element-350">
            <div className="inline-flex items-center space-x-2 bg-primary/5 text-primary px-4 py-2 rounded-full text-xs md:text-sm font-bold tracking-wide uppercase" data-id="element-351">
              <ShieldCheck size={16} data-id="element-352" />
              <span data-id="element-353">Serving Communities Across Maryland</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-extrabold text-charcoal leading-[1.1] text-balance" data-id="element-354">
              A Safe Place to <span className="text-primary" data-id="element-355">Call Home</span>
            </h1>

            <p className="text-lg md:text-xl text-muted leading-relaxed max-w-xl" data-id="element-356">
              Providing supportive independent living opportunities for adults
              throughout Maryland in a comfortable, community-focused
              environment.
            </p>

            <div className="flex items-stretch gap-2 sm:gap-4" data-id="element-357">
              <Button size="lg" variant="primary" to="/homes" className="min-w-0 flex-1 whitespace-nowrap px-2.5 py-3 text-[11px] sm:flex-none sm:px-8 sm:py-4 sm:text-lg" data-id="element-358">
                Check Availability
              </Button>
              <Button size="lg" variant="outline" to="/contact" className="min-w-0 flex-1 whitespace-nowrap px-2.5 py-3 text-[11px] sm:flex-none sm:px-8 sm:py-4 sm:text-lg" data-id="element-359">
                Schedule a Tour
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4" data-id="element-360">
              {['Safe Housing', 'Community Focused', 'Referral Friendly', 'Serving Maryland'].map(item => <div key={item} className="flex items-center space-x-2 text-sm font-semibold text-charcoal/70" data-id="element-361">
                  <CheckCircle2 size={16} className="text-accent" data-id="element-362" />
                  <span data-id="element-363">{item}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Content - Overlapping Imagery */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative" data-id="element-364">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] lg:aspect-square" data-id="element-365">
              <img src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&q=80&w=1000" alt="Modern Residential Home" className="w-full h-full object-cover" data-id="element-366" />
            </div>

            {/* Floating Cards */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-10 -left-10 z-20 bg-white p-6 rounded-2xl shadow-premium hidden md:block" data-id="element-367">
              <div className="flex items-center space-x-4" data-id="element-368">
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center text-accent" data-id="element-369">
                  <Home size={24} data-id="element-370" />
                </div>
                <div data-id="element-371">
                  <div className="text-2xl font-bold text-charcoal" data-id="element-372">15+</div>
                  <div className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-373">
                    Homes Statewide
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute top-1/2 -right-1 z-20 bg-primary text-white p-6 rounded-2xl shadow-premium hidden md:block" data-id="element-374">
              <div className="flex items-center space-x-4" data-id="element-375">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-376">
                  <Users size={24} data-id="element-377" />
                </div>
                <div data-id="element-378">
                  <div className="text-2xl font-bold" data-id="element-379">100+</div>
                  <div className="text-xs font-bold text-white/70 uppercase tracking-wider" data-id="element-380">
                    Residents Supported
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            x: [0, 5, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} className="absolute -bottom-8 left-1/4 z-20 bg-white p-4 rounded-xl shadow-premium border border-accent/20" data-id="element-381">
              <div className="flex items-center space-x-2" data-id="element-382">
                <div className="flex -space-x-2" data-id="element-383">
                  {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-384">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="Resident" data-id="element-385" />
                    </div>)}
                </div>
                <div className="text-sm font-bold text-charcoal" data-id="element-386">
                  <div className="flex text-accent" data-id="element-387">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={12} fill="currentColor" data-id="element-388" />)}
                  </div>
                  Beds Available Today
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Search Section */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="relative z-30 -mt-16 px-6" data-id="element-389">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-390">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end" data-id="element-391">
            <div className="space-y-2" data-id="element-392">
              <label className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-393">
                County
              </label>
              <div className="relative" data-id="element-394">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} data-id="element-395" />
                <select value={searchFilters.county} onChange={e => setSearchFilters({
                ...searchFilters,
                county: e.target.value
              })} className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary appearance-none" data-id="element-396">
                  <option data-id="element-397">All Counties</option>
                  <option data-id="element-398">Baltimore City</option>
                  <option data-id="element-399">Baltimore County</option>
                  <option data-id="element-400">Montgomery County</option>
                </select>
              </div>
            </div>
            <div className="space-y-2" data-id="element-401">
              <label className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-402">
                Gender
              </label>
              <div className="relative" data-id="element-403">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} data-id="element-404" />
                <select value={searchFilters.gender} onChange={e => setSearchFilters({
                ...searchFilters,
                gender: e.target.value
              })} className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary appearance-none" data-id="element-405">
                  <option data-id="element-406">Any Gender</option>
                  <option data-id="element-407">Male</option>
                  <option data-id="element-408">Female</option>
                </select>
              </div>
            </div>
            <div className="space-y-2" data-id="element-409">
              <label className="text-xs font-bold text-muted uppercase tracking-wider" data-id="element-410">
                Move-in Date
              </label>
              <div className="relative" data-id="element-411">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} data-id="element-412" />
                <input type="date" value={searchFilters.moveInDate} onChange={e => setSearchFilters({
                ...searchFilters,
                moveInDate: e.target.value
              })} className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" data-id="element-413" />
              </div>
            </div>
            <Button onClick={handleSearch} className="w-full py-4 flex items-center justify-center space-x-2" data-id="element-414">
              <Search size={20} data-id="element-415" />
              <span data-id="element-416">Search Availability</span>
            </Button>
          </div>
        </div>
      </motion.section>

      {/* Welcome Section */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-20 md:py-32 px-6" data-id="element-417">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-418">
          <div className="relative" data-id="element-419">
            <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-420">
              <img src="https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=1000" alt="Bright Modern Living Space" className="w-full aspect-[4/3] object-cover" data-id="element-421" />
            </div>
            <div className="absolute -bottom-10 -right-10 bg-accent p-10 rounded-2xl hidden lg:block max-w-xs" data-id="element-422">
              <Quote className="text-white/40 mb-4" size={40} data-id="element-423" />
              <p className="text-white font-medium italic leading-relaxed" data-id="element-424">
                "We don't just provide a room; we provide a community where
                every resident feels valued and safe."
              </p>
            </div>
          </div>

          <div className="space-y-6 md:space-y-8" data-id="element-425">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-charcoal leading-tight" data-id="element-426">
              Welcome to Birds Nest Independent Living
            </h2>
            <div className="space-y-6 text-lg text-muted leading-relaxed" data-id="element-427">
              <p data-id="element-428">
                At Birds Nest, we believe that everyone deserves a safe, stable,
                and supportive place to call home. Our mission is to bridge the
                gap between healthcare and housing by providing premium
                independent living accommodations for adults throughout
                Maryland.
              </p>
              <p data-id="element-429">
                Our homes are more than just residences—they are vibrant
                communities designed to foster independence while providing the
                safety net of professional management and 24/7 support access.
              </p>
            </div>
            <div className="pt-4" data-id="element-430">
              <Button variant="outline" size="lg" to="/about" data-id="element-431">
                Learn More About Our Mission
              </Button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Services Preview */}
      <motion.section initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }} className="py-20 md:py-32 bg-primary px-6 relative overflow-hidden" data-id="element-432">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none" data-id="element-433">
          <div className="absolute top-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-white rounded-full blur-[80px] md:blur-[100px]" data-id="element-434" />
          <div className="absolute bottom-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-accent rounded-full blur-[80px] md:blur-[100px]" data-id="element-435" />
        </div>

        <div className="max-w-7xl mx-auto text-center mb-12 md:mb-20" data-id="element-436">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6" data-id="element-437">
            Our Core Services
          </h2>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto" data-id="element-438">
            Comprehensive housing solutions tailored to the unique needs of our
            residents and referral partners.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-439">
          {[{
          title: 'Independent Living',
          icon: Home,
          desc: 'High-quality shared housing with all utilities included and fully furnished spaces.'
        }, {
          title: 'Safe Community',
          icon: ShieldCheck,
          desc: 'Secure environments with 24/7 emergency support and professional property management.'
        }, {
          title: 'Housing Placement',
          icon: MapPin,
          desc: 'Rapid placement services for individuals transitioning from hospitals or treatment centers.'
        }, {
          title: 'Community Resources',
          icon: Users,
          desc: 'Connections to local healthcare, transportation, and social services to support independence.'
        }].map((service, idx) => <motion.div key={service.title} whileHover={{
          y: -10
        }} className="bg-white/10 backdrop-blur-md border border-white/10 p-8 rounded-2xl group transition-all duration-300 flex flex-col h-full" data-id="element-440">
              <div className="w-14 h-14 bg-accent rounded-xl flex items-center justify-center text-charcoal mb-6 group-hover:scale-110 transition-transform shrink-0" data-id="element-441">
                <service.icon size={28} data-id="element-442" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-443">
                {service.title}
              </h3>
              <p className="text-white/60 mb-6 leading-relaxed" data-id="element-444">
                {service.desc}
              </p>
              <Link to="/services" className="inline-flex items-center text-accent font-bold hover:underline mt-auto" data-id="element-445">
                Learn More <ArrowRight size={16} className="ml-2" data-id="element-446" />
              </Link>
            </motion.div>)}
        </div>
      </motion.section>

      {/* Featured Properties */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-20 md:py-32 px-6 bg-background" data-id="element-447">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-16 gap-6 md:gap-8" data-id="element-448">
          <div className="max-w-2xl" data-id="element-449">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-charcoal mb-4 md:mb-6" data-id="element-450">
              Featured Properties
            </h2>
            <p className="text-muted text-base md:text-lg" data-id="element-451">
              Explore our modern, comfortable homes across Maryland. Each
              location is carefully selected for safety and accessibility.
            </p>
          </div>
          <Button variant="outline" to="/homes" className="w-full md:w-auto" data-id="element-452">
            View All Properties
          </Button>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-453">
          {[{
          name: 'The Silver Spring Nest',
          city: 'Silver Spring',
          county: 'Montgomery',
          beds: '2 Available',
          status: 'Available',
          img: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&q=80&w=800'
        }, {
          name: 'Baltimore Harbor View',
          city: 'Baltimore',
          county: 'Baltimore City',
          beds: 'Limited',
          status: 'Limited',
          img: 'https://images.unsplash.com/photo-1576941089067-2de3c901e126?auto=format&fit=crop&q=80&w=800'
        }, {
          name: 'Towson Community Home',
          city: 'Towson',
          county: 'Baltimore County',
          beds: 'Waitlist',
          status: 'Waitlist',
          img: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800'
        }].map(property => <div key={property.name} className="bg-white rounded-2xl overflow-hidden shadow-premium group hover:shadow-premium-hover transition-all duration-300 border border-gray-100" data-id="element-454">
              <div className="relative h-64 overflow-hidden" data-id="element-455">
                <img src={property.img} alt={property.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-456" />
                <div className={cn('absolute top-4 right-4 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider', property.status === 'Available' ? 'bg-green-500 text-white' : property.status === 'Limited' ? 'bg-accent text-charcoal' : 'bg-gray-500 text-white')} data-id="element-457">
                  {property.status}
                </div>
              </div>
              <div className="p-8" data-id="element-458">
                <div className="text-xs font-bold text-accent uppercase tracking-widest mb-2" data-id="element-459">
                  {property.county} County
                </div>
                <h3 className="text-2xl font-bold text-charcoal mb-4" data-id="element-460">
                  {property.name}
                </h3>
                <div className="flex items-center text-muted mb-6 space-x-4 text-sm" data-id="element-461">
                  <div className="flex items-center" data-id="element-462">
                    <MapPin size={16} className="mr-1" data-id="element-463" /> {property.city}
                  </div>
                  <div className="flex items-center" data-id="element-464">
                    <Users size={16} className="mr-1" data-id="element-465" /> {property.beds}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-466">
                  <Button variant="primary" size="sm" to="/contact" data-id="element-467">
                    Apply Now
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openPropertyDetails(property)} data-id="element-468">
                    View Details
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </motion.section>

      {/* Referral Partners */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-20 md:py-32 px-6 bg-primary text-white" data-id="element-469">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-470">
          <div className="space-y-6 md:space-y-8" data-id="element-471">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold leading-tight" data-id="element-472">
              Trusted Partner for Referral Agencies
            </h2>
            <p className="text-white/70 text-lg md:text-xl leading-relaxed" data-id="element-473">
              We work closely with hospitals, case managers, and social service
              organizations to provide seamless housing transitions for their
              clients.
            </p>
            <div className="space-y-4" data-id="element-474">
              {['Fast placement turnarounds', 'Transparent availability reporting', 'Comprehensive move-in coordination', 'Regular resident progress updates'].map(item => <div key={item} className="flex items-center space-x-3" data-id="element-475">
                  <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-charcoal" data-id="element-476">
                    <CheckCircle2 size={14} data-id="element-477" />
                  </div>
                  <span className="font-medium" data-id="element-478">{item}</span>
                </div>)}
            </div>
            <div className="pt-6" data-id="element-479">
              <Button variant="accent" size="lg" to="/referrals" data-id="element-480">
                Submit a Referral
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6" data-id="element-481">
            <div className="space-y-6" data-id="element-482">
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-483">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-484">24h</h4>
                <p className="text-white/60 font-medium" data-id="element-485">Response Time</p>
              </div>
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-486">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-487">50+</h4>
                <p className="text-white/60 font-medium" data-id="element-488">Active Partners</p>
              </div>
            </div>
            <div className="space-y-6 mt-12" data-id="element-489">
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-490">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-491">100%</h4>
                <p className="text-white/60 font-medium" data-id="element-492">Compliance</p>
              </div>
              <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-493">
                <h4 className="text-3xl font-bold text-accent mb-2" data-id="element-494">MD</h4>
                <p className="text-white/60 font-medium" data-id="element-495">Statewide Coverage</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Inquiry Form */}
      <motion.section initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="py-20 md:py-32 px-6" data-id="element-496">
        <div className="max-w-7xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col lg:flex-row" data-id="element-497">
          <div className="lg:w-1/2 p-8 md:p-12 lg:p-20 bg-secondary/5" data-id="element-498">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-charcoal mb-6 md:mb-8" data-id="element-499">
              Ready to find a safe place to call home?
            </h2>
            <p className="text-muted text-base md:text-lg mb-8 md:mb-12" data-id="element-500">
              Fill out the form below and our admissions team will contact you
              within 24 hours to discuss availability and schedule a tour.
            </p>

            <div className="space-y-8" data-id="element-501">
              <div className="flex items-start space-x-4" data-id="element-502">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-primary shrink-0" data-id="element-503">
                  <Phone size={24} data-id="element-504" />
                </div>
                <div data-id="element-505">
                  <h4 className="font-bold text-charcoal" data-id="element-506">Call Us Directly</h4>
                  <p className="text-muted" data-id="element-507">(410) 555-0123</p>
                </div>
              </div>
              <div className="flex items-start space-x-4" data-id="element-508">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-primary shrink-0" data-id="element-509">
                  <Mail size={24} data-id="element-510" />
                </div>
                <div data-id="element-511">
                  <h4 className="font-bold text-charcoal" data-id="element-512">Email Admissions</h4>
                  <p className="text-muted" data-id="element-513">admissions@birdsnestliving.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2 p-8 md:p-12 lg:p-20" data-id="element-514">
            <form className="space-y-6" data-id="element-515">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-516">
                <div className="space-y-2" data-id="element-517">
                  <label className="text-sm font-bold text-charcoal" data-id="element-518">
                    Full Name
                  </label>
                  <input type="text" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="John Doe" data-id="element-519" />
                </div>
                <div className="space-y-2" data-id="element-520">
                  <label className="text-sm font-bold text-charcoal" data-id="element-521">
                    Phone Number
                  </label>
                  <input type="tel" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="(410) 000-0000" data-id="element-522" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-523">
                <label className="text-sm font-bold text-charcoal" data-id="element-524">
                  Email Address
                </label>
                <input type="email" className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="john@example.com" data-id="element-525" />
              </div>
              <div className="space-y-2" data-id="element-526">
                <label className="text-sm font-bold text-charcoal" data-id="element-527">
                  Interested In
                </label>
                <select className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary appearance-none" data-id="element-528">
                  <option data-id="element-529">Independent Living</option>
                  <option data-id="element-530">Referral Partnership</option>
                  <option data-id="element-531">General Inquiry</option>
                </select>
              </div>
              <div className="space-y-2" data-id="element-532">
                <label className="text-sm font-bold text-charcoal" data-id="element-533">
                  Message
                </label>
                <textarea rows={4} className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-primary" placeholder="How can we help you?" data-id="element-534"></textarea>
              </div>
              <Button className="w-full py-4 text-lg" data-id="element-535">Send Inquiry</Button>
            </form>
          </div>
        </div>
      </motion.section>

      <PropertyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} property={selectedProperty} data-id="element-536" />
    </div>;
}